import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import type { MiceProjectView } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { currentLocale, t } from '@web/i18n';
import {
  Button,
  Card,
  DataTable,
  EmptyHint,
  formatRawDate,
  PageSectionHead,
  Scaffold,
  StackedBar,
  ToneChip,
  useOrgContext,
} from '@web/ui/core';
import { useAuthz } from '@web/ui/core/hooks/use_authz';
import { fireAndForget } from '@web/ui/core/utils';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Dias de hoje (no navegador) até a data AAAA-MM-DD, contando só a data. */
function daysUntil(isoDate: string): number {
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const [y = 0, m = 1, d = 1] = isoDate.split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - today) / DAY_MS);
}

const hasEnded = (p: MiceProjectView): boolean =>
  p.eventDate !== null && daysUntil(p.eventEnd ?? p.eventDate) < 0;

function countdown(p: MiceProjectView): string | null {
  if (!p.eventDate) return null;
  if (hasEnded(p)) return t('mice.events.ended');
  const days = daysUntil(p.eventDate);
  return days <= 0 ? t('mice.events.today') : t('mice.events.inDays', { count: days });
}

function progressOf(p: MiceProjectView) {
  const c = p.counts;
  const fmt = new Intl.NumberFormat(currentLocale());
  const buying = c.queued + c.purchasing + c.awaiting_approval + c.awaiting_issuance;
  const problem = c.failed + c.indeterminate;
  return { issued: c.issued, buying, problem, fmt };
}

function PeriodCell({ project: p }: Readonly<{ project: MiceProjectView }>) {
  const period = p.eventDate
    ? t('mice.project.period', {
        start: formatRawDate(p.eventDate, null),
        end: formatRawDate(p.eventEnd ?? p.eventDate, null),
      })
    : t('mice.events.noDate');
  const left = countdown(p);
  return (
    <div>
      <div className="text-foreground">{period}</div>
      {left ? <div className="text-micro text-muted-foreground">{left}</div> : null}
    </div>
  );
}

function ProgressCell({ project: p }: Readonly<{ project: MiceProjectView }>) {
  const { issued, buying, problem, fmt } = progressOf(p);
  const values = {
    issued: fmt.format(issued),
    buying: fmt.format(buying),
    problem: fmt.format(problem),
  };
  return (
    <div className="flex w-56 flex-col gap-1.5">
      <StackedBar
        label={t('mice.events.bar', values)}
        segments={[
          { key: 'issued', value: issued, tone: 'green' },
          { key: 'buying', value: buying, tone: 'ai' },
          { key: 'problem', value: problem, tone: 'red' },
          { key: 'rest', value: p.counts.ready + p.counts.validating, tone: 'gray' },
        ]}
      />
      <span className="text-micro text-muted-foreground">{t('mice.events.progress', values)}</span>
    </div>
  );
}

function PendingCell({ project: p }: Readonly<{ project: MiceProjectView }>) {
  const { issued, buying, problem } = progressOf(p);
  const done = issued > 0 && buying === 0 && problem === 0 && p.counts.ready === 0;
  return (
    <div className="flex flex-wrap gap-1.5">
      {p.phaseCounts.approval > 0 ? (
        <ToneChip tone="ai">{t('mice.events.approve', { count: p.phaseCounts.approval })}</ToneChip>
      ) : null}
      {problem > 0 ? (
        <ToneChip tone="red">{t('mice.events.problem', { count: problem })}</ToneChip>
      ) : null}
      {p.premisesCheckedAt === null ? (
        <ToneChip tone="amber">{t('mice.events.checkPremises')}</ToneChip>
      ) : null}
      {done ? <ToneChip tone="green">{t('mice.events.allIssued')}</ToneChip> : null}
    </div>
  );
}

/** Pendências somadas dos eventos em andamento (os encerrados ficam de fora). */
function PendingCards({ projects }: Readonly<{ projects: readonly MiceProjectView[] }>) {
  const live = projects.filter((p) => !hasEnded(p));
  const fmt = new Intl.NumberFormat(currentLocale());
  const cards = [
    { key: 'approve', value: live.reduce((n, p) => n + p.phaseCounts.approval, 0) },
    // Os mesmos pedidos do chip "N com problema" (falha + indeterminado), não a fase Atenção, que
    // também conta cadastro inválido, duplicado ou sem solução, onde ainda não há compra.
    { key: 'problem', value: live.reduce((n, p) => n + progressOf(p).problem, 0) },
    { key: 'premises', value: live.filter((p) => p.premisesCheckedAt === null).length },
  ] as const;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map((c) => (
        <Card key={c.key}>
          <div role="group" aria-label={t(`mice.events.cards.${c.key}`)} className="p-4">
            <div className="text-micro text-muted-foreground">
              {t(`mice.events.cards.${c.key}`)}
            </div>
            <div className="text-h-display font-medium text-foreground">{fmt.format(c.value)}</div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Colunas da lista; o nome do evento é o botão que o abre (a linha inteira também abre). */
const columnsFor = (open: (projectId: string) => void): ColumnDef<MiceProjectView>[] => [
  {
    id: 'event',
    header: () => t('mice.events.col.event'),
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="text-left font-medium text-foreground hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              open(row.original.id);
            }}
          >
            {row.original.name}
          </button>
          {row.original.origin === 'self_service' ? (
            <ToneChip tone="gray">{t('mice.events.selfService')}</ToneChip>
          ) : null}
          {row.original.purchasesClosedAt === null ? null : (
            <ToneChip tone="amber">{t('mice.events.purchasesClosed')}</ToneChip>
          )}
        </div>
        <div className="text-micro text-muted-foreground">
          {row.original.client?.name ?? row.original.company.name}
        </div>
      </div>
    ),
  },
  {
    id: 'period',
    header: () => t('mice.events.col.period'),
    cell: ({ row }) => <PeriodCell project={row.original} />,
  },
  {
    id: 'progress',
    header: () => t('mice.events.col.progress'),
    cell: ({ row }) => <ProgressCell project={row.original} />,
  },
  {
    id: 'pending',
    header: () => t('mice.events.col.pending'),
    cell: ({ row }) => <PendingCell project={row.original} />,
  },
];

export function MiceProjectsPage(): React.JSX.Element {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const authz = useAuthz();
  const projects = useQuery({
    queryKey: queryKeys.mice.projects(orgId),
    queryFn: () => miceRepository.listProjects(orgId),
  });
  const open = (projectId: string): void => {
    fireAndForget(
      navigate({ to: '/orgs/$orgSlug/mice/$projectId', params: { orgSlug, projectId } }),
    );
  };

  return (
    <Scaffold
      body={
        <div className="mx-auto flex w-full max-w-reading-w flex-col gap-6">
          <div className="flex items-start justify-between gap-4">
            <PageSectionHead title={t('mice.events.title')} sub={t('mice.projects.sub')} />
            {authz.has('mice:manage') ? (
              <Button
                variant="primary"
                onClick={() => {
                  fireAndForget(navigate({ to: '/orgs/$orgSlug/mice/new', params: { orgSlug } }));
                }}
              >
                {t('mice.projects.new')}
              </Button>
            ) : null}
          </div>
          {projects.data?.length === 0 ? <EmptyHint>{t('mice.projects.empty')}</EmptyHint> : null}
          {projects.data && projects.data.length > 0 ? (
            <PendingCards projects={projects.data} />
          ) : null}
          {projects.data && projects.data.length > 0 ? (
            <div className="overflow-x-auto">
              <DataTable
                columns={columnsFor(open)}
                data={projects.data}
                getRowId={(p) => p.id}
                onRowClick={(p) => {
                  open(p.id);
                }}
              />
            </div>
          ) : null}
        </div>
      }
    />
  );
}
